import React from "react";
import { isNegativeVeil, negativeVeilPath } from "./lib/veilGeometry";
import { groupShape, nodesFor, placeCaption } from "./lib/overlays";
import { isSelected as matches } from "./lib/selection";
import { ORPHAN, groupColour, sequenceColour } from "./lib/palette";

// Everything an overlay draws, in one layer under the spheres — except the
// sequence, which has to sit above them to be legible.

function GroupShape({ shape, colour, testId }) {
  const common = {
    className: "overlay__shape",
    fill: colour,
    fillOpacity: 0.13,
    stroke: colour,
    strokeOpacity: 0.5,
    "data-testid": testId
  };
  if (!shape) return null;
  if (shape.type === "circle") return <circle {...common} cx={shape.cx} cy={shape.cy} r={shape.r} />;
  if (shape.type === "path") return <path {...common} d={shape.d} />;
  return (
    <rect
      {...common}
      x={shape.x}
      y={shape.y}
      width={shape.width}
      height={shape.height}
      rx={shape.rx}
    />
  );
}

function ShapeOverlay({ overlay, sephirothBySlug, radius }) {
  return (
    <g className={`overlay overlay--${overlay.channel}`} data-testid={`overlay-${overlay.slug}`}>
      {overlay.groups.map((group) => (
        <GroupShape
          key={group.slug}
          shape={groupShape(group, sephirothBySlug, radius, overlay.channel)}
          colour={groupColour(group)}
          testId={`overlay-group-${overlay.slug}-${group.slug}`}
        />
      ))}
    </g>
  );
}

function MarkerOverlay({ overlay, viewBox, selected, onSelectGroup }) {
  const { x, width } = viewBox;
  return (
    <g className="overlay overlay--marker" data-testid={`overlay-${overlay.slug}`}>
      {overlay.groups
        .filter((group) => group.markerY !== null && group.markerY !== undefined)
        .map((group) => {
          const colour = groupColour(group);
          const isSelected = matches(selected, "marker", group.slug);
          const veil = isNegativeVeil(group);
          // A marker with an x as well as a y is a PLACE, not a line across the
          // tree. Da'ath is the only one: it is drawn hollow, dashed and
          // unnumbered, the same way an uncounted sephirah is drawn, because
          // that is exactly what it is not quite.
          const point = group.markerX !== null && group.markerX !== undefined;
          const d = veil ? negativeVeilPath(group, viewBox) : null;
          if (point) {
            return (
              <g
                key={group.slug}
                className={isSelected ? "marker marker--selected" : "marker"}
                data-testid={`marker-${group.slug}`}
              >
                <circle
                  className="overlay__marker-hit"
                  cx={group.markerX}
                  cy={group.markerY}
                  r={(viewBox.radius || 7) + 4}
                  onClick={() => onSelectGroup && onSelectGroup(group)}
                  data-testid={`marker-hit-${group.slug}`}
                />
                <circle
                  className="overlay__marker-point"
                  cx={group.markerX}
                  cy={group.markerY}
                  r={viewBox.radius || 7}
                  fill="none"
                  stroke={colour}
                />
              </g>
            );
          }
          return (
            <g
              key={group.slug}
              className={isSelected ? "marker marker--selected" : "marker"}
              data-testid={`marker-${group.slug}`}
            >
              {/* A 0.5-wide dashed stroke is not something you can hit with a
                  finger. The wide transparent copy underneath is the target,
                  the same trick the paths use; the visible rule is left alone
                  so nothing about the drawing changes. */}
              {veil ? (
                <path
                  className="overlay__marker-hit"
                  d={d}
                  fill="none"
                  onClick={() => onSelectGroup && onSelectGroup(group)}
                  data-testid={`marker-hit-${group.slug}`}
                />
              ) : (
                <line
                  className="overlay__marker-hit"
                  x1={x + 6}
                  y1={group.markerY}
                  x2={x + width - 6}
                  y2={group.markerY}
                  onClick={() => onSelectGroup && onSelectGroup(group)}
                  data-testid={`marker-hit-${group.slug}`}
                />
              )}
              {veil ? (
                <path
                  className="overlay__marker-rule overlay__negative-veil"
                  d={d}
                  fill="none"
                  stroke={colour}
                />
              ) : (
                <line
                  className="overlay__marker-rule"
                  x1={x + 6}
                  y1={group.markerY}
                  x2={x + width - 6}
                  y2={group.markerY}
                  stroke={colour}
                />
              )}
            </g>
          );
        })}
    </g>
  );
}

// Geometry shared by the sequence renderers.
function segmentsOf(steps, hasPathNetwork) {
  const drawn = steps.filter((s) => s.node);
  return drawn.slice(1).map((to, i) => ({
    key: `${drawn[i].slug}-${to.slug}`,
    from: drawn[i],
    to,
    // Only meaningful where there IS a path network to contrast against.
    orphan: hasPathNetwork && to.segmentHasPath === false,
    pathSlug: to.segmentPathSlug
  }));
}

// Two arrows fit the compact paths; three make the longer diagonals and
// horizontals readable without forcing the user to hunt for one tiny marker.
// The geometry has a natural gap between paths around 38 units and those over
// 52, so 46 cleanly separates the two groups.
const LONG_SEGMENT_MIN = 46;

function arrowHeads(line, flip, fullLength) {
  const startX = flip ? line.x2 : line.x1;
  const startY = flip ? line.y2 : line.y1;
  const endX = flip ? line.x1 : line.x2;
  const endY = flip ? line.y1 : line.y2;
  const dx = endX - startX;
  const dy = endY - startY;
  const length = Math.hypot(dx, dy) || 1;
  const ux = dx / length;
  const uy = dy / length;
  const count = fullLength >= LONG_SEGMENT_MIN ? 3 : 2;
  // Tav has only eight visible units after rim insets. Scale its two heads
  // down enough to keep both distinct and outside the spheres.
  const size = Math.min(3.4, Math.max(1.7, length / (count * 2.1)));

  return Array.from({ length: count }, (_, index) => {
    const fraction = (index + 1) / (count + 1);
    const tipX = startX + dx * fraction;
    const tipY = startY + dy * fraction;
    const backX = tipX - ux * size;
    const backY = tipY - uy * size;
    const nx = -uy * size * 0.5;
    const ny = ux * size * 0.5;
    return `${tipX},${tipY} ${backX + nx},${backY + ny} ${backX - nx},${backY - ny}`;
  });
}

// Segments run RIM TO RIM. Centre to centre, the line starts inside the circle
// and covers the numeral.
export function inset(x1, y1, x2, y2, by) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const length = Math.hypot(dx, dy) || 1;
  const ux = (dx / length) * by;
  const uy = (dy / length) * by;
  return { x1: x1 + ux, y1: y1 + uy, x2: x2 - ux, y2: y2 - uy };
}

// The Lightning Flash does NOT follow the paths: eight of its nine segments lie
// on real paths and one does not. So it is drawn as its own polyline through
// the sephiroth, never by highlighting a sequence of paths.
//
// One ELEMENT PER SEGMENT rather than one long path: the Serpent has five
// discontinuities, so a single path with one dashoffset would stall the reveal
// on empty space. Same mechanism for both, and it is what makes per-segment
// labels and the dashed Abyss segment possible at all.
function SephirahSequence({
  overlay,
  steps,
  step,
  colour,
  hasPathNetwork,
  radius,
  layer
}) {
  const drawn = steps.filter((s) => s.node);
  const segments = segmentsOf(steps, hasPathNetwork);
  const current = steps[step - 1];

  if (layer === "over") {
    return current && current.node ? (
      <g className="overlay overlay--sequence-cursor" data-testid={`cursor-${overlay.slug}`}>
        <circle
          className="overlay__cursor"
          cx={current.node.x}
          cy={current.node.y}
          r={12}
          fill="none"
          stroke={colour}
          data-testid="sequence-cursor"
        />
      </g>
    ) : null;
  }

  return (
    <g className="overlay overlay--sequence" data-testid={`overlay-${overlay.slug}`}>
      <polyline
        className="overlay__trace-ghost"
        points={drawn.map((s) => `${s.node.x},${s.node.y}`).join(" ")}
        fill="none"
        stroke={colour}
        data-testid="sequence-trace"
      />

      {segments.map((segment, index) => {
        // Segment `index` joins step index+1 to step index+2.
        const revealed = step >= index + 2;
        const stroke = segment.orphan ? ORPHAN : colour;
        const line = inset(
          segment.from.node.x,
          segment.from.node.y,
          segment.to.node.x,
          segment.to.node.y,
          (radius || 7) + 1
        );
        const fullLength = Math.hypot(
          segment.to.node.x - segment.from.node.x,
          segment.to.node.y - segment.from.node.y
        );
        const arrows = arrowHeads(line, false, fullLength);
        return (
          <g
            key={segment.key}
            className={`seq${segment.orphan ? " seq--orphan" : ""}${revealed ? " seq--revealed" : ""}`}
            data-testid={segment.orphan ? `orphan-${segment.key}` : `segment-${segment.key}`}
          >
            {segment.orphan && (
              <line
                className="overlay__orphan-knockout"
                x1={line.x1}
                y1={line.y1}
                x2={line.x2}
                y2={line.y2}
              />
            )}
            {/* pathLength="100" makes the dash values percentages, so no
                getTotalLength(), no ref, no re-measure when the SVG scales. */}
            <line
              className={segment.orphan ? "seq__segment seq__segment--orphan" : "seq__segment"}
              x1={line.x1}
              y1={line.y1}
              x2={line.x2}
              y2={line.y2}
              stroke={stroke}
              pathLength="100"
              strokeDasharray={segment.orphan ? "6 4.8" : "100"}
              strokeDashoffset={revealed ? 0 : 100}
            />
            {/* Direction is visible as soon as the lens is selected. The step
                still controls the animated stroke and strengthens arrows that
                the sequence has reached. */}
            {arrows.map((points, arrowIndex) => (
              <polygon
                key={`${segment.key}-arrow-${arrowIndex}`}
                className={`seq__arrow ${revealed ? "seq__arrow--revealed" : "seq__arrow--pending"}`}
                points={points}
                fill={stroke}
                data-testid={`arrow-${segment.key}-${arrowIndex + 1}`}
              />
            ))}
          </g>
        );
      })}
    </g>
  );
}

// The Serpent is not a continuous walk — only sixteen of its twenty-one
// consecutive pairs share a sephirah — so it is drawn as an ordered traversal,
// never as a route. Where consecutive steps do not touch, the jump is drawn as
// a faint dotted connector so the discontinuity is visible rather than
// mysterious.
function PathSequence({ overlay, steps, step, colour, radius, layer }) {
  const current = steps[step - 1];
  const drawn = steps.filter((s) => s.node);
  const r = (radius || 7) + 1;

  if (layer === "over") {
    return current && current.node ? (
      <g className="overlay overlay--sequence-cursor" data-testid={`cursor-${overlay.slug}`}>
        <g data-testid="sequence-cursor">
          {/* The current segment glows in ITS OWN colour. Recolouring every
              traversed path green threw away the King-scale colours, which are
              data. */}
          <line
            className="overlay__traversal-current"
            {...inset(current.node.x1, current.node.y1, current.node.x2, current.node.y2, r)}
            stroke={current.node.colorKingHex}
          />
        </g>
      </g>
    ) : null;
  }

  return (
    <g className="overlay overlay--traversal" data-testid={`overlay-${overlay.slug}`}>
      {drawn.map((s, index) => {
        const revealed = step >= index + 1;
        const line = inset(s.node.x1, s.node.y1, s.node.x2, s.node.y2, r);
        // The Serpent ASCENDS: every arrow points at whichever end is higher.
        const fullLength = Math.hypot(s.node.x2 - s.node.x1, s.node.y2 - s.node.y1);
        const arrows = arrowHeads(line, line.y1 > line.y2, fullLength);
        return (
          <g key={s.slug} data-testid={`serpent-segment-${s.slug}`}>
            <line
              className={`seq__segment overlay__traversed${revealed ? " seq--revealed" : ""}`}
              {...line}
              // Its own colour, never the overlay's.
              stroke={s.node.colorKingHex}
              pathLength="100"
              strokeDasharray="100"
              strokeDashoffset={revealed ? 0 : 100}
            />
            {arrows.map((points, arrowIndex) => (
              <polygon
                key={`${s.slug}-arrow-${arrowIndex}`}
                className={`seq__arrow ${revealed ? "seq__arrow--revealed" : "seq__arrow--pending"}`}
                points={points}
                fill={s.node.colorKingHex}
                data-testid={`serpent-arrow-${s.slug}-${arrowIndex + 1}`}
              />
            ))}
          </g>
        );
      })}

      {drawn.slice(1).map((to, i) => {
        const from = drawn[i];
        // Only around the active window: five faint connectors permanently on
        // screen is five more things to read.
        if (Math.abs(i + 1 - step) > 1 && Math.abs(i + 2 - step) > 1) return null;
        const shares =
          from.node.fromSlug === to.node.fromSlug ||
          from.node.fromSlug === to.node.toSlug ||
          from.node.toSlug === to.node.fromSlug ||
          from.node.toSlug === to.node.toSlug;
        if (shares) return null;
        return (
          <line
            key={`jump-${from.slug}-${to.slug}`}
            className="seq__jump"
            x1={from.node.labelX}
            y1={from.node.labelY}
            x2={to.node.labelX}
            y2={to.node.labelY}
            stroke={colour}
            data-testid={`jump-${from.slug}-${to.slug}`}
          />
        );
      })}
    </g>
  );
}

// Every caption, badge and endpoint note. Lives in the LABEL layer, painted
// last, per the six-layer order.
export function SequenceLabels({
  overlay,
  steps,
  step,
  hasPathNetwork,
  showCaptions,
  pathsBySlug,
  obstacles,
  sephiroth,
  radius,
  viewBox
}) {
  if (!overlay || steps.length === 0 || !showCaptions) return null;
  const colour = sequenceColour(overlay);
  const isPathSequence = steps[0].kind === "path";

  if (isPathSequence) {
    const current = steps[step - 1];
    return (
      <g className="seq-labels" data-testid="sequence-labels" aria-hidden="true">
        {steps.map((s, index) => index).filter((index) => Math.abs(index + 1 - step) <= 1)
          .map((index) => steps[index]).map((s, _i) => {
          const index = steps.indexOf(s);
          return s.node ? (
            // A FILLED CIRCULAR badge for serpent order — never the rectangular
            // plate the path number already uses. A line reading "1" and "32"
            // in the same shape is the confusion to design out.
            <g key={`badge-${s.slug}`} data-testid={`serpent-badge-${s.slug}`}>
              <circle
                className="seq__badge"
                cx={s.node.labelX + 5.4}
                cy={s.node.labelY - 4.2}
                r={2.6}
                fill={colour}
                opacity={index + 1 === step ? 1 : 0.45}
              />
              <text
                className="seq__badge-text"
                x={s.node.labelX + 5.4}
                y={s.node.labelY - 3.3}
                textAnchor="middle"
              >
                {index + 1}
              </text>
            </g>
          ) : null;
        })}
      </g>
    );
  }

  const drawn = steps.filter((s) => s.node);
  const segments = segmentsOf(steps, hasPathNetwork);

  return (
    <g className="seq-labels" data-testid="sequence-labels" aria-hidden="true">
      {segments.map((segment, index) => {
        const revealed = step >= index + 2;
        if (!revealed) return null;
        // Only the active window, the same rule the order badges follow. Nine
        // captions plus 22 plates plus 10 names does not fit — the solver
        // proved it, having searched 208 positions for "along 27 · Peh" and
        // found none clear. Each segment still names its path as you reach it.
        if (Math.abs(index + 2 - step) > 1) return null;
        // "along 11 · Aleph" for the eight that have one; the ninth says so.
        const path = segment.pathSlug && pathsBySlug ? pathsBySlug.get(segment.pathSlug) : null;
        const text = segment.orphan
          ? "no path · the Abyss"
          : path
            ? `along ${path.number} · ${path.letterName}`
            : "";
        if (!text) return null;
        // Solved, not nudged: slide along the segment until it clears every
        // plate and every sphere.
        const at = placeCaption(segment.from.node, segment.to.node, text, {
          obstacles,
          sephiroth,
          radius,
          viewBox
        });
        return (
          <text
            key={`caption-${segment.key}`}
            className={segment.orphan ? "seq__caption seq__caption--orphan" : "seq__caption"}
            x={at.x}
            y={at.y}
            textAnchor="middle"
            fill={segment.orphan ? ORPHAN : colour}
            data-testid={`caption-${segment.key}`}
          >
            {text}
          </text>
        );
      })}

      {/* Endpoint captions: where the descent begins and where it ends. */}
      {drawn.length > 1 && (
        <>
          {/* To the SIDE. Above and below are already taken by the sphere's
              own name, which these would sit exactly on top of. */}
          <text
            className="seq__endpoint"
            x={drawn[0].node.x - 12}
            y={drawn[0].node.y + 1}
            textAnchor="end"
            fill={colour}
            data-testid="sequence-begins"
          >
            {`Begins: ${drawn[0].node.name}`}
          </text>
          <text
            className="seq__endpoint"
            x={drawn[drawn.length - 1].node.x + 12}
            y={drawn[drawn.length - 1].node.y + 1}
            textAnchor="start"
            fill={colour}
            data-testid="sequence-ends"
          >
            {`Ends: ${drawn[drawn.length - 1].node.name}`}
          </text>
        </>
      )}
    </g>
  );
}

// `steps` is resolved ONCE, in TreeOfLife, and handed down. It used to be
// recomputed here on every render — twice, since this layer draws both under
// and over the spheres — and a third time for the labels, from the same two
// Maps, for the same answer.
export function SequenceLayer({
  overlay,
  steps = [],
  step,
  hasPathNetwork,
  radius,
  layer = "under"
}) {
  if (!overlay || steps.length === 0) return null;
  const colour = sequenceColour(overlay);
  const isPathSequence = steps[0].kind === "path";

  return isPathSequence ? (
    <PathSequence
      overlay={overlay}
      steps={steps}
      step={step}
      colour={colour}
      radius={radius}
      layer={layer}
    />
  ) : (
    <SephirahSequence
      overlay={overlay}
      steps={steps}
      step={step}
      colour={colour}
      hasPathNetwork={hasPathNetwork}
      radius={radius}
      layer={layer}
    />
  );
}

// The relationship between serpent order and path number, computed rather than
// asserted: it is exactly the kind of sentence that goes stale.
export function sequenceLegend(overlay, steps) {
  if (!overlay || steps.length === 0) return null;
  if (steps[0].kind !== "path") return null;

  const first = steps[0].node;
  const last = steps[steps.length - 1].node;
  if (!first || !last) return null;

  const jumps = steps.slice(1).filter((to, i) => {
    const from = steps[i];
    if (!from.node || !to.node) return false;
    return ![from.node.fromSlug, from.node.toSlug].some((slug) =>
      [to.node.fromSlug, to.node.toSlug].includes(slug)
    );
  }).length;

  return (
    `Step 1 is path ${first.number}; step ${steps.length} is path ${last.number}. ` +
    `It counts down. ${jumps} of the ${steps.length - 1} moves join paths that share ` +
    "no sephirah, drawn as dotted jumps — this is an order of study, not a route."
  );
}

// Bands, hulls and markers, all beneath the paths and spheres.
export default function OverlayLayer({
  overlays,
  sephirothBySlug,
  viewBox,
  selected = null,
  onSelectGroup = null
}) {
  if (overlays.length === 0) return null;

  return (
    <g className="tree__overlays" aria-hidden="true" data-testid="overlay-layer">
      {overlays.map((overlay) =>
        overlay.channel === "marker" ? (
          <MarkerOverlay
            key={overlay.slug}
            overlay={overlay}
            viewBox={viewBox}
            selected={selected}
            onSelectGroup={onSelectGroup}
          />
        ) : (
          <ShapeOverlay
            key={overlay.slug}
            overlay={overlay}
            sephirothBySlug={sephirothBySlug}
            radius={viewBox.radius}
          />
        )
      )}
    </g>
  );
}

export { nodesFor };
